import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { generateRandomAlphanumeric } from 'utils/generateRandomAlphanumeric';
import { fetchMatches } from 'features/case/redux/fetchMatches';
import { useUpdateUserSettings } from 'features/common/redux/updateUserSettings';
import { useSetAISearchState } from 'features/case/redux/setAISearchState';
import { useFetchMatches } from 'features/case/redux/fetchMatches';
import { getInitials } from 'utils/getInitials';
import { useSelector } from 'react-redux';
import { selectCurrentCaseId, selectUserId, selectUserName } from 'common/selectors';
import history from 'common/history';
import { searchCenterLocation } from '../enums';
import T from 'i18n';
import { generateAnswers } from 'features/case/redux/generateAnswers';
import { generateAnswersWithCitationsData } from 'features/case/redux/generateAnswersWithCitationsData';
import { askAI } from 'features/case/redux/askAI';
import { removeFormatSuffix } from '../utils/formatUtils';

interface UseAIHandlerProps {
  initialFilters?: {
    location?: object[];
    files?: object[];
    isCompareMode?: boolean;
    phrase?: string;
    searchHistoryId?: string;
    question?: string;
    triggerSearch?: boolean;
    promptCategory?: string;
    promptName?: string;
  };
  refreshSearchHistory?: () => void;
}

const useAIHandler = (props?: UseAIHandlerProps) => {
  const dispatch = useDispatch();
  const refreshSearchHistory = props?.refreshSearchHistory;
  const {
    updateAISearchMatches,
    setAISearchMatches,
    clearAISearchState,
    setAISearchAnswerChat,
    setAISearchSuggestedQuestions,
    setDirectAnswersFilters,
    searchMatchesInput,
    searchAnsChat,
    searchSuggestedQuestions,
    directAnswersFilters,
  } = useSetAISearchState();
  const { clearMatches } = useFetchMatches();
  const perfectScrollbarRef = useRef<any | null>(null);
  const { userSettings, updateUserSettings } = useUpdateUserSettings();
  const userName = useSelector(selectUserName);
  const caseId = useSelector(selectCurrentCaseId);
  const currentUserId = useSelector(selectUserId);

  // Default filters
  const defaultFilters = {
    location: searchMatchesInput?.location?.length > 0 ? searchMatchesInput?.location : [],
    files: searchMatchesInput?.files?.length > 0 ? searchMatchesInput?.files : [],
    isCompareMode: searchMatchesInput?.isCompare || false,
    phrase: searchMatchesInput?.phrase || '',
    searchHistoryId: searchMatchesInput?.searchHistoryId || '',
    question: '',
    triggerSearch: false,
    promptCategory: undefined,
    promptName: undefined,
  };
  // Merge initial filters with defaults
  const initialState = props?.initialFilters
    ? { ...defaultFilters, ...props.initialFilters }
    : defaultFilters;
  const [filters, setFilters] = useState(initialState);
  const [selectedMatches, setSelectedMatches] = useState<any>([]);
  const [isNewSearch, setIsNewSearch] = useState(
    searchMatchesInput?.[0]?.matches?.length > 0 ? false : true,
  );
  const [showNoMatchesMessage, setShowNoMatchesMessage] = useState(false);
  const [showSearchInp, setShowSearchInp] = useState(false);
  const [storedOutputType, setStoredOutputType] = useState<string>('list');
  const userInitials = getInitials(userName);

  const showSuggestedQuestions = true; // Set true to be always enabled

  const isAnswersMode =
    !isNewSearch &&
    (searchMatchesInput.find((itm: any) => itm.matches) ||
      (searchMatchesInput.length === 0 && searchAnsChat.length > 0));
  const matchesList = searchMatchesInput.find((itm: any) => itm.matches)?.matches || [];
  const isSummaryMode =
    !isNewSearch &&
    searchAnsChat?.length > 0 &&
    searchAnsChat?.find((ans: any) => ans.command === 'summarize');

  const matches = [
    !isSummaryMode && {
      id: 'selectedMatches',
      name: 'Selected Matches',
      type: 'matches',
      matches: selectedMatches,
    },
    !isSummaryMode && { id: 'allMatches', name: 'All Matches', type: 'matches' },
    isSummaryMode && { id: 'summary', name: 'Summary', type: 'summary' },
    ...(
      (!isSummaryMode &&
        matchesList &&
        matchesList.length > 0 &&
        matchesList.reduce((acc: any, itm: any) => {
          const existingFile = acc.find((f: any) => f.id === itm.fileId);
          if (existingFile) {
            existingFile.matches.push(itm);
            return acc;
          } else {
            return [
              ...acc,
              { id: itm.fileId, name: itm.fileName, type: 'matches', matches: [itm] },
            ];
          }
        }, [])) ||
      []
    ).sort((a: any, b: any) => b.matches.length - a.matches.length),
  ].filter(Boolean);

  const updateScrollbar = () => {
    if (perfectScrollbarRef.current) {
      const container = perfectScrollbarRef.current;
      container.scrollTop = container.scrollHeight;
    }
  };

  const createMatchesPayload = (topicId: string, inp: any, showMatchesResp: boolean): object => {
    return {
      id: topicId,
      showMatchesResp,
      phrase: inp.phrase,
      location: inp.location,
      files: inp.files,
      user: userInitials,
      contributor: null,
      options: {
        numMatches: (userSettings.aiSettings && userSettings.aiSettings.numMatches) || 1000,
        multiQuery: (userSettings.aiSettings && userSettings.aiSettings.multiQuery) || false,
        maxMarginalRelevance:
          (userSettings.aiSettings && userSettings.aiSettings.maxMarginalRelevance) || false,
        index: (userSettings.aiSettings && userSettings.aiSettings.index) || '',
      },
    };
  };

  const handleMatchesResponse = (
    topicId: string,
    res: any,
    inpVal?: string,
    fileSidebarAskAI?: boolean,
    isCompareMode?: boolean,
  ) => {
    const phraseForSideBar = fileSidebarAskAI ? inpVal : null;
    if (res.router === 'topic' || res.router === 'question') {
      setFilters({
        phrase:
          phraseForSideBar ||
          (filters.question
            ? filters.question
            : res.router === 'question'
              ? inpVal
                ? inpVal
                : filters.phrase
              : showSuggestedQuestions && res.results.length > 0
                ? 'Can you suggest 5 questions based on these extracts. List the questions without numbering them.' // Default suggested questions
                : ''),
        location: res.results.length > 0 ? [matches[matches.length - 1]] : [],
        files: [],
        isCompareMode: false,
        searchHistoryId: res.searchHistoryId,
        question: '',
        triggerSearch:
          res.router === 'question' ||
          (showSuggestedQuestions && res.router === 'topic' && res.results.length > 0),
        promptCategory: undefined,
        promptName: undefined,
      });
      updateAISearchMatches(topicId, {
        matches: res.results,
        isCompare: isCompareMode,
        searchHistoryId: res.searchHistoryId,
        compare: res.compare,
        relevantFields: res.relevantFields,
      });
      setSelectedMatches([]);
    } else if (res.router === 'lookup') {
      setFilters((prevState: any) => ({
        ...prevState,
        phrase: '',
        location: [],
        files: res.files,
        searchHistoryId: res.searchHistoryId,
        question: '',
        triggerSearch: false,
      }));
      updateAISearchMatches(topicId, { documents: res.files });
      setSelectedMatches([]);
    } else if (res.router === 'summarize') {
      updateAISearchMatches(topicId, {
        router: res.router,
        ...(res.results.length > 0 && { showSummarizeMsg: true }),
        matches: res.results,
        relevantFields: res.relevantFields,
      });
    } else if (res.router === 'summarize_transcript') {
      updateAISearchMatches(topicId, {
        router: res.router,
        showSummarizeMsg: true,
        fileId: res.fileId,
        matches: res.results,
        relevantFields: res.relevantFields,
      });
      setFilters((prevState: any) => ({
        ...prevState,
        location: res.results.length > 0 ? [matches[matches.length - 1]] : [],
      }));
    }
  };

  const processDirectAnswers = (
    searchQuery: string,
    searchAnsChatData: any,
    filtersData: any,
    outputType: string,
    askAiSearchType?: string,
  ) => {
    const savedVersion = userSettings.aiSettings && (userSettings.aiSettings as any).version;
    const version = savedVersion && savedVersion !== '' ? savedVersion : 'latest';
    const elementId = generateRandomAlphanumeric(8);
    const historyId = searchAnsChatData.length > 1 ? searchAnsChatData[1].searchHistoryId : '';
    if (isNewSearch) setIsNewSearch(false);
    const safeFiltersData = filtersData ?? {};
    // Check if we're in compare mode
    const isCompareMode = safeFiltersData.isCompareMode === true;

    const filtersPayload = {
      folders: Array.isArray(safeFiltersData.location) ? safeFiltersData.location : [],
      files: isCompareMode ? [] : Array.isArray(safeFiltersData.files) ? safeFiltersData.files : [],
    };
    // if (!searchAnsChatData.length) {
    setAISearchAnswerChat([
      {
        id: elementId + '-Question',
        text: searchQuery,
        rawText: searchQuery,
        user: userInitials,
        contributor: null,
        typingEffect: false,
        isDirectAnswerHistory: true,
        options: {},
      },
      {
        id: elementId + '-AnswerReq',
        text: T.translate('case.searchingText'),
        user: 'botIcon',
        contributor: null,
        typingEffect: false,
        isDirectAnswerHistory: true,
        firstQuery: false,
      },
    ]);
    setTimeout(() => updateScrollbar(), 0);
    // }

    // Capture prompt metadata before clearing
    const promptCategory = filtersData?.promptCategory;
    const promptName = filtersData?.promptName;

    // Clear prompt metadata immediately so it won't be used in subsequent calls
    if (promptCategory || promptName) {
      setFilters({
        ...filters,
        promptCategory: undefined,
        promptName: undefined,
      });
    }

    dispatch(
      generateAnswersWithCitationsData({
        searchQuery,
        historyId,
        filters: filtersPayload,
        outputType,
        version,
        ...(isCompareMode &&
          Array.isArray(safeFiltersData.files) &&
          safeFiltersData.files.length > 0 && {
            compare: {
              filesToCompare: safeFiltersData.files.map((file: any) => ({
                id: file.id,
                name: file.name,
              })),
            },
          }),
        ...(promptCategory && { promptCategory }),
        ...(promptName && { promptName }),
      }),
    )
      .then((res: any) => {
        if (!res || !res.answer || !res.searchHistoryId) {
          clearAISearchState();
          if (!isNewSearch) setIsNewSearch(true);
        } else {
          setAISearchAnswerChat([
            {
              id: elementId + '-Answer',
              text: getResponseText(res),
              rawText: getResponseText(res),
              answerId: res?.answerId,
              citations: res.citations,
              relevantFiles: res?.relevantFiles,
              files: res?.filters?.files,
              filesToCompare: res?.compare?.filesToCompare,
              location: res?.filters?.folders,
              responseType: res.responseType,
              isDirectAnswerHistory: true,
              user: 'botIcon',
              contributor: null,
              showMatches: false,
              typingEffect: true,
              firstQuery: false,
              searchHistoryId: !historyId ? res.searchHistoryId : historyId,
            },
          ]);

          // Store filters data for this response
          const newFilterObject = {
            id: res?.answerId,
            files: Array.isArray(res?.filters?.files) ? res.filters.files : [],
            location: Array.isArray(res?.filters?.folders) ? res.filters.folders : [],
            filesToCompare: Array.isArray(res?.compare?.filesToCompare)
              ? res.compare.filesToCompare
              : [],
          };

          // Only add if there are files or folders
          if (
            newFilterObject.files.length > 0 ||
            newFilterObject.location.length > 0 ||
            newFilterObject.filesToCompare.length > 0
          ) {
            const existingFilters = Array.isArray(directAnswersFilters) ? directAnswersFilters : [];
            setDirectAnswersFilters([...existingFilters, newFilterObject]);
          }

          // Refresh search history after successful response
          if (refreshSearchHistory && !historyId) {
            refreshSearchHistory();
          }
          if (!historyId) {
            const searchHistoryId = res.searchHistoryId;
            setFilters({ ...filters, searchHistoryId: res.searchHistoryId });
            if (res.searchHistoryId && askAiSearchType !== 'bundle') {
              history.push(
                history.location.pathname.replace(/\/ai-tools.*/, ``) +
                  `/ai-tools${searchHistoryId ? `/${searchHistoryId}` : ''}`,
              );
            }
          }
          setIsNewSearch(false);
        }
      })
      .catch((e: any) => {
        setAISearchAnswerChat([
          {
            id: elementId + '-Answer',
            text: e.body ? e.body : T.translate('generic.unexpectedAiApiError'),
            rawText: e.body ? e.body : T.translate('generic.unexpectedAiApiError'),
            citations: null,
            relevantFiles: [],
            files: [],
            location: [],
            answerId: null,
            responseType: null,
            isDirectAnswerHistory: true,
            user: 'botIcon',
            contributor: null,
            showMatches: false,
            typingEffect: false,
            firstQuery: false,
            searchHistoryId: historyId || '',
          },
        ]);
        setTimeout(() => updateScrollbar(), 0);
      });
  };

  const processTopic = (
    showMatchesResp: boolean,
    fileSidebarAskAI?: boolean,
    inpVal?: string,
    sHisId?: string,
    fileData?: any,
    topicFilters?: any,
    outputType?: string,
  ) => {
    // Store the outputType for later use in processQuestions
    if (outputType) {
      setStoredOutputType(outputType);
    }
    if (isNewSearch) setIsNewSearch(false);
    const topicId = generateRandomAlphanumeric(8);
    const updatedFilters = { ...topicFilters, phrase: inpVal || topicFilters.phrase };
    setAISearchMatches(createMatchesPayload(topicId, updatedFilters, showMatchesResp));
    if (fileSidebarAskAI) {
      const elementId = generateRandomAlphanumeric(8);
      setAISearchAnswerChat([
        {
          id: elementId + '-Question',
          text: removeFormatSuffix(inpVal ? inpVal : topicFilters.phrase),
          rawText: removeFormatSuffix(inpVal ? inpVal : topicFilters.phrase),
          user: userInitials,
          contributor: null,
          typingEffect: false,
          options: {},
        },
        {
          id: elementId + '-AnswerReq',
          text: T.translate('case.searchingText'),
          user: 'botIcon',
          contributor: null,
          typingEffect: false,
          firstQuery: false,
        },
      ]);
    }

    // Check if we're in compare mode
    const isCompareMode = topicFilters.isCompareMode && topicFilters?.files?.length > 0;
    let fileProps;
    if (fileSidebarAskAI && isCompareMode && fileData?.id) {
      fileProps = [{ id: fileData?.id, name: fileData?.name }, ...topicFilters.files];
    } else if (fileSidebarAskAI && fileData?.id) {
      fileProps = [{ id: fileData?.id, name: fileData?.name }];
    } else {
      fileProps = topicFilters.files;
    }
    const payload: any = {
      searchTerm: inpVal ? inpVal : topicFilters.phrase,
      searchLocation: isCompareMode ? [] : topicFilters.location,
      files: isCompareMode ? [] : fileProps,
      matchCentric: true,
      omitFiles: true,
    };
    // Add source when originating from sidebar OR current path is not already within /ai-tools
    const topicPathName = history.location?.pathname || '';
    if (fileSidebarAskAI || !topicPathName.includes('/ai-tools')) {
      payload.source = 'sidebar';
    }

    // Add compare field if in compare mode
    if (isCompareMode) {
      payload.compare = {
        files: fileProps.map((file: any) => file.id),
      };
    }

    // Add prompt metadata if available
    if (topicFilters.promptCategory) {
      payload.promptCategory = topicFilters.promptCategory;
    }
    if (topicFilters.promptName) {
      payload.promptName = topicFilters.promptName;
    }

    // Clear prompt metadata immediately after adding to payload
    if (topicFilters.promptCategory || topicFilters.promptName) {
      setFilters({
        ...filters,
        promptCategory: undefined,
        promptName: undefined,
      });
    }

    dispatch(fetchMatches(payload))
      .then((res: any) => {
        if (!res || !res.results || res.results.length === 0) {
          clearAISearchState();
          if (!isNewSearch) setIsNewSearch(true);
          if (!showNoMatchesMessage) setShowNoMatchesMessage(true);
          return;
        } else if (showNoMatchesMessage) {
          setShowNoMatchesMessage(false);
        }
        // Refresh search history after successful response
        if (refreshSearchHistory) {
          refreshSearchHistory();
        }
        const searchHistoryId = sHisId || res.searchHistoryId;
        handleMatchesResponse(
          topicId,
          { ...res, searchHistoryId },
          inpVal,
          fileSidebarAskAI,
          isCompareMode,
        );
        if (!fileSidebarAskAI) {
          history.push(
            history.location.pathname.replace(/\/ai-tools.*/, ``) +
              `/ai-tools${searchHistoryId ? `/${searchHistoryId}` : ''}`,
          );
        }
      })
      .catch(() => {
        clearAISearchState();
        if (!fileSidebarAskAI) {
          setIsNewSearch(true);
        }
      });
  };

  const processQuestions = (
    isSuggestedQuestions?: any,
    inpVal?: string,
    fileSidebarAskAI?: boolean,
    firstChat?: boolean,
    outputType?: string,
    questionFilters?: any,
  ) => {
    // Use provided filters or fall back to hook's state filters
    const activeFilters = questionFilters || filters;
    const elementId = generateRandomAlphanumeric(8);
    if (isSuggestedQuestions) {
      setAISearchSuggestedQuestions({
        id: elementId + '-Answer',
        searching: true,
      });
    } else if (!fileSidebarAskAI || !firstChat) {
      setAISearchAnswerChat([
        {
          id: elementId + '-Question',
          text: removeFormatSuffix(inpVal ? inpVal : activeFilters.phrase),
          rawText: removeFormatSuffix(inpVal ? inpVal : activeFilters.phrase),
          user: userInitials,
          contributor: null,
          typingEffect: false,
          options: {
            //To-Do remove options from here
            sourcePrompt: (userSettings.aiSettings && userSettings.aiSettings.sourcePrompt) || '',
            systemPrompt: (userSettings.aiSettings && userSettings.aiSettings.systemPrompt) || '',
            useDefaultPrompt:
              (userSettings.aiSettings && userSettings.aiSettings.useDefaultPrompt) || true,
          },
        },
        {
          id: elementId + '-AnswerReq',
          text: T.translate('case.searchingText'),
          rawText: T.translate('case.searchingText'),
          user: 'botIcon',
          contributor: null,
          typingEffect: false,
          firstQuery: false,
        },
      ]);
      setTimeout(() => updateScrollbar(), 0);
    }
    const historyID =
      activeFilters.searchHistoryId ||
      (searchMatchesInput &&
        searchMatchesInput.length > 0 &&
        searchMatchesInput[0].searchHistoryId) ||
      (() => {
        const path = history.location && history.location.pathname ? history.location.pathname : '';
        const match = path.match(/ai-tools\/([^/]+)/);
        return match ? match[1] : null;
      })();
    const questionsPathName = history.location?.pathname || '';
    const includeSource = fileSidebarAskAI || !questionsPathName.includes('/ai-tools');

    // Capture prompt metadata before clearing
    const promptCategory = activeFilters.promptCategory;
    const promptName = activeFilters.promptName;

    // Clear prompt metadata immediately so it won't be used in subsequent calls
    if (promptCategory || promptName) {
      setFilters({
        ...filters,
        promptCategory: undefined,
        promptName: undefined,
      });
    }

    dispatch(
      generateAnswers({
        summary: isSummaryMode
          ? searchAnsChat.find((itm: any) => itm.command === 'summarize').text
          : [],
        searchQuery: inpVal ? inpVal : activeFilters.phrase,
        searchHistoryId: historyID,
        outputType: outputType || 'list', // Add outputType with default value
        ...(includeSource && { source: 'sidebar' }),
        ...(promptCategory && { promptCategory }),
        ...(promptName && { promptName }),
      }),
    )
      .then((res: any) => {
        if (!res?.cancelled) {
          const newSearchBtn = document.getElementById('new-search-button');
          //const searchContainerElem = document.getElementById('search-container');
          if (
            (newSearchBtn || fileSidebarAskAI) &&
            res &&
            //searchContainerElem?.ariaLabel === res.searchHistoryId && //check if the search container is still the same
            (!res.async || (res.async && res.answer))
          ) {
            if (isSuggestedQuestions) {
              setAISearchSuggestedQuestions({
                ...searchSuggestedQuestions,
                searching: false,
                questions: res.sources.length > 0 ? res.sources : null,
              });
            } else {
              setAISearchAnswerChat([
                {
                  id: elementId + '-Answer',
                  text: getResponseText(res),
                  rawText: getRawResponseText(res),
                  user: 'botIcon',
                  contributor: null,
                  showMatches: false,
                  typingEffect: true,
                  matches:
                    selectedMatches.length > 0 ? selectedMatches : searchMatchesInput.matches,
                  firstQuery: false,
                  answerId: res?.answerId,
                  relevantFiles: res?.relevantFiles,
                  responseType: res.responseType,
                  searchHistoryId: historyID,
                },
              ]);
            }
          }
          updateScrollbar();
        }
      })
      .catch((err: any) => {
        if (!isSuggestedQuestions) {
          const newSearchBtn = document.getElementById('new-search-button');
          if (newSearchBtn) {
            setAISearchAnswerChat([
              {
                id: elementId + '-Error',
                typingEffect: false,
                firstQuery: false,
                text:
                  err && typeof err.body === 'string'
                    ? err.body
                    : T.translate('case.answersErrorMsg'),
                user: 'botIcon',
                contributor: null,
              },
            ]);
            updateScrollbar();
          }
        } else {
          setAISearchSuggestedQuestions({
            ...searchSuggestedQuestions,
            searching: false,
            questions: null,
          }); // Clear suggested questions
        }
      });
  };

  const getResponseText = (res: any) => {
    if (res.sources && res.sources.length > 0) {
      return res.sources;
    }
    if (res.summary) {
      return res.summary;
    }
    if (res.answer) {
      return Array.isArray(res.answer) ? res.answer[0] : res.answer;
    }
    return T.translate('case.noAnswerFoundMsg');
  };

  const getRawResponseText = (res: any) => {
    if (res.rawAnswer) {
      return res.rawAnswer;
    }
    return '';
  };

  const askAIHandler = (command: string, fileId: string | string[], summaryType: string) => {
    if (isNewSearch) setIsNewSearch(false);
    const elementId = generateRandomAlphanumeric(8);
    setAISearchAnswerChat([
      {
        id: elementId + '-Question',
        text: `${command} ${fileId}`,
        user: userInitials,
        contributor: null,
        typingEffect: false,
        options: {
          //To-Do remove options from here
          sourcePrompt: (userSettings.aiSettings && userSettings.aiSettings.sourcePrompt) || '',
          systemPrompt: (userSettings.aiSettings && userSettings.aiSettings.systemPrompt) || '',
          useDefaultPrompt:
            (userSettings.aiSettings && userSettings.aiSettings.useDefaultPrompt) || true,
        },
      },
      {
        id: elementId + '-AnswerReq',
        text: T.translate('case.searchingText'),
        user: 'botIcon',
        contributor: null,
        typingEffect: false,
      },
    ]);
    setTimeout(() => updateScrollbar(), 0);
    dispatch(askAI({ command, fileId, summaryType }))
      .then((res: any) => {
        if (!showSearchInp) setShowSearchInp(true);
        history.push(
          history.location.pathname.replace(/\/ai-tools.*/, ``) +
            `/ai-tools/${res.searchHistoryId ? res.searchHistoryId : ''}`,
        );
        setAISearchAnswerChat([
          {
            id: elementId + '-Answer',
            text: getResponseText(res),
            rawText: getRawResponseText(res),
            command: res.command,
            submissions: res.submissions,
            rulings: res.rulings,
            user: 'botIcon',
            contributor: null,
            showMatches: false,
            typingEffect: true,
            matches: selectedMatches.length > 0 ? selectedMatches : searchMatchesInput.matches,
          },
        ]);
        setFilters({ ...filters, location: [{ id: 'summary', name: 'Summary', type: 'matches' }] });
      })
      .catch((err: any) => {
        setAISearchAnswerChat([
          {
            id: elementId + '-Error',
            typingEffect: false,
            text:
              err && typeof err.body === 'string' ? err.body : T.translate('case.answersErrorMsg'),
            user: 'botIcon',
            contributor: null,
          },
        ]);
        updateScrollbar();
      });
  };

  const saveSearchInputs = (phrase: string) => {
    // Clean the phrase before saving
    const cleanPhrase = removeFormatSuffix(phrase);

    const aiInputs = userSettings?.aiSearchInputs || [];
    const searchInputs = aiInputs[caseId] ? aiInputs[caseId] : [];
    let updatedSearchInputs = [];
    if (
      filters.location.find(
        (loc: any) =>
          loc.id === searchCenterLocation.allMatches ||
          loc.id === searchCenterLocation.selectedMatches,
      )
    ) {
      const existingTopicEntry = searchInputs.find(
        (entry: any) => entry.topic === searchMatchesInput.phrase,
      );
      const existingEntry = existingTopicEntry?.questions.find(
        (question: string) => question === cleanPhrase,
      );
      updatedSearchInputs = existingEntry
        ? aiInputs
        : {
            ...aiInputs,
            [caseId]: [
              {
                topic: existingTopicEntry ? existingTopicEntry.topic : searchMatchesInput.phrase,
                questions: [cleanPhrase, ...(existingTopicEntry?.questions || [])],
              },
              ...searchInputs.filter((entry: any) => entry.topic !== searchMatchesInput.phrase),
            ],
          };
    } else {
      const existingTopicEntry = searchInputs.find((entry: any) => entry.topic === cleanPhrase);
      updatedSearchInputs = existingTopicEntry
        ? aiInputs
        : { ...aiInputs, [caseId]: [{ topic: cleanPhrase, questions: [] }, ...searchInputs] };
    }
    updateUserSettings({
      userSettings: { ...userSettings, aiSearchInputs: updatedSearchInputs },
    });
  };

  const handleSearchHistoryResponse = (
    res: any,
    searchHistoryItmId: string,
    fileSidebarAskAI?: boolean,
  ) => {
    if (res && res.length === 0) {
      setAISearchAnswerChat([
        {
          isError: true,
          text: 'You don"t have access to this search history item',
        },
      ]);
      if (isNewSearch) setIsNewSearch(false);
      return;
    }
    const isDirectAnswers = res[0] && res[0].command === 'answers-direct';
    if (isNewSearch) setIsNewSearch(false);
    if (!showSearchInp && !fileSidebarAskAI) setShowSearchInp(true);
    let matchesRes: any;
    let originalAnswersRes: any;
    if (isDirectAnswers) {
      originalAnswersRes = res.filter(
        (itm: any) => itm.command === 'answers-direct' || itm.action === 'answers-direct',
      );
      // Persist direct-answers filters (if present)
      const directFilters = res
        .filter((itm: any) => itm.filters && (itm.filters.files || itm.filters.folders))
        .map((itm: any) => ({
          id: itm.id,
          files: Array.isArray(itm.filters?.files) ? itm.filters.files : [],
          location: Array.isArray(itm.filters?.folders) ? itm.filters.folders : [],
          filesToCompare: Array.isArray(itm.compare?.filesToCompare)
            ? itm.compare.filesToCompare
            : [],
        }))
        .filter(
          (itm: any) =>
            itm.files.length > 0 || itm.location.length > 0 || itm.filesToCompare.length > 0,
        );

      if (directFilters.length > 0) {
        setDirectAnswersFilters(directFilters);
      }
    } else {
      matchesRes = res.find((itm: any) => itm.action === 'matches');
      if (!matchesRes) matchesRes = res.find((itm: any) => itm.command === 'question');
      originalAnswersRes = res.filter((itm: any) => itm.action === 'answers');
    }
    const answersRes = originalAnswersRes.reduce((acc: any, itm: any) => {
      const contributor = itm.createdBy.id !== currentUserId ? itm.createdBy.name : null;
      const itemOwnerInitials = itm.createdBy ? getInitials(itm.createdBy.name) : userInitials;
      return [
        ...acc,
        {
          id: itm.id + '-Question',
          text: removeFormatSuffix(itm.query),
          isDirectAnswerHistory: isDirectAnswers,
          user: itemOwnerInitials,
          contributor,
          typingEffect: false,
          searchHistoryId: itm.id,
          options: {
            sourcePrompt: itm.sourcePrompt,
            systemPrompt: itm.systemPrompt,
            useDefaultPrompt: itm.useDefaultPrompt,
          },
        },
        {
          id: itm.id + '-Answer',
          text: getResponseText(itm),
          isDirectAnswerHistory: isDirectAnswers,
          searchHistoryId: itm.id,
          rawText: isDirectAnswers ? getResponseText(itm) : getRawResponseText(itm),
          user: 'botIcon',
          command: itm.summary ? 'summarize' : '', //TO-DO use command from response need BE support itm.command,
          matches: matchesRes?.matches || [],
          citations: itm.citations || [],
          relevantFiles: itm.relevantFiles || [],
          files: itm.filters?.files || [],
          filesToCompare: itm.compare?.filesToCompare || [],
          location: itm.filters?.folders || [],
          answerId: itm?.answerId || '',
          responseType: itm?.responseType || '',
          showMatches: false,
          typingEffect: false,
          contributor: null,
        },
      ];
    }, []);

    const isSummaryModeHistory =
      (!matchesRes || matchesRes?.length === 0) && originalAnswersRes?.length > 0;
    const isAllMatches =
      matchesRes &&
      originalAnswersRes &&
      originalAnswersRes.length > 0 &&
      matchesRes.matches.length > 0 &&
      setFilters({
        searchHistoryId: searchHistoryItmId,
        phrase: '',
        location: isSummaryModeHistory ? [{ id: 'summary', name: 'Summary', type: 'matches' }] : [],
        files: [],
        isCompareMode: false,
        question: '',
        triggerSearch: false,
        promptCategory: undefined,
        promptName: undefined,
      });
    setSelectedMatches(isAllMatches || originalAnswersRes.length === 0 ? [] : matchesRes?.matches);
    const matchesOwnerInitials = matchesRes?.createdBy
      ? getInitials(matchesRes.createdBy.name)
      : userInitials;
    const contributor =
      matchesRes && matchesRes.createdBy.id !== currentUserId ? matchesRes.createdBy.name : null;
    if (matchesRes && originalAnswersRes.length > 0) {
      setAISearchMatches({
        phrase: matchesRes.query,
        location: matchesRes.filters.folders,
        files: matchesRes.filters?.files || [],
        user: matchesOwnerInitials,
        compare: matchesRes.compare,
        contributor,
        matches: matchesRes.matches,
        options: matchesRes.options,
        showMatchesResp: true,
      });
    } else {
      setIsNewSearch(true);
      clearMatches();
    }

    setAISearchAnswerChat(answersRes);
    if (!fileSidebarAskAI) {
      history.push(
        history.location.pathname.replace(/\/ai-tools.*/, ``) + `/ai-tools/${searchHistoryItmId}`,
      );
    }
  };

  const handleChangeFilters = (updatedItem: any) => {
    setFilters({ ...filters, ...updatedItem });
    if (isAnswersMode && updatedItem['location']) {
      if (updatedItem['location'][0]?.id === searchCenterLocation.allMatches) {
        setSelectedMatches([]);
      } else if (updatedItem['location']?.id === searchCenterLocation.selectedMatches) {
        setSelectedMatches(updatedItem['location'].matches);
      } else {
        setSelectedMatches(updatedItem['location'].map((loc: any) => loc.matches).flat());
      }
    }
  };

  return {
    selectedMatches,
    setSelectedMatches,
    filters,
    setFilters,
    processTopic,
    isNewSearch,
    setIsNewSearch,
    processQuestions,
    processDirectAnswers,
    askAIHandler,
    showSearchInp,
    matches,
    saveSearchInputs,
    handleSearchHistoryResponse,
    updateScrollbar,
    handleChangeFilters,
    isAnswersMode,
    perfectScrollbarRef,
    showSuggestedQuestions,
    showNoMatchesMessage,
    storedOutputType,
  };
};

export default useAIHandler;
